import { console } from '@liveblocks/core';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useRerender } from './use-rerender.js';

function useControllableState(defaultValue, value, onChange) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const isControlled = value !== void 0;
  const wasControlled = useRef(isControlled);
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && wasControlled.current !== isControlled) {
      console.warn(
        `A component is changing from ${wasControlled.current ? "controlled" : "uncontrolled"} to ${isControlled ? "controlled" : "uncontrolled"}.`
      );
    }
    wasControlled.current = isControlled;
  }, [isControlled]);
  const currentValue = isControlled ? value : uncontrolledValue;
  const setValue = useCallback(
    (value2) => {
      if (isControlled) {
        return onChange?.(value2);
      } else {
        setUncontrolledValue(value2);
        return onChange?.(value2);
      }
    },
    [isControlled, onChange]
  );
  return [currentValue, setValue];
}
function useSemiControllableState(value, onChange) {
  const [uncontrolledValue, setUncontrolledValue] = useState(value);
  const lastChange = useRef("controlled");
  const lastValue = useRef(value);
  const [rerender] = useRerender();
  if (!Object.is(lastValue.current, value)) {
    lastValue.current = value;
    lastChange.current = "controlled";
  }
  const setValue = useCallback(
    (value2) => {
      lastChange.current = "uncontrolled";
      setUncontrolledValue(value2);
      rerender();
      onChange?.(value2);
    },
    [onChange, rerender]
  );
  const currentValue = lastChange.current === "uncontrolled" ? uncontrolledValue : value;
  return [currentValue, setValue];
}

export { useControllableState, useSemiControllableState };
//# sourceMappingURL=use-controllable-state.js.map
